import { useState } from 'react';
import { Download, Trash2, Eye, FileText, PhoneCall, MessageSquare, Mail } from 'lucide-react';
import { useFetch } from '../../hooks/useFetch';
import { leadsApi } from '../../api/leadsApi';
import { Modal } from '../../components/ui/Modal';
import './LeadsManager.css';

const STATUT_META = {
  nouveau: { label: 'Nouveau', tone: 'primary' },
  en_cours: { label: 'En cours', tone: 'secondary' },
  traite: { label: 'Traité', tone: 'green' },
  archive: { label: 'Archivé', tone: 'muted' },
};
const STATUTS = Object.keys(STATUT_META);

const TYPE_ICONS = {
  devis: FileText,
  rappel: PhoneCall,
  contact: MessageSquare,
  newsletter: Mail,
};

function TypeIcon({ type, ...props }) {
  const Icon = TYPE_ICONS[type] || Mail;
  return <Icon {...props} />;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr.replace(' ', 'T')).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatutSelect({ lead, meta, onChange }) {
  return (
    <select
      value={lead.statut}
      onChange={(e) => onChange(lead, e.target.value)}
      className={`statut-badge statut-badge--${meta.tone}`}
      onClick={(e) => e.stopPropagation()}
    >
      {STATUTS.map((s) => (
        <option key={s} value={s}>{STATUT_META[s].label}</option>
      ))}
    </select>
  );
}

export function LeadsManager() {
  const [filtreStatut, setFiltreStatut] = useState('');
  const { data: leads, loading, refetch } = useFetch(() => leadsApi.list(filtreStatut || undefined), [filtreStatut]);
  const [notesEnCours, setNotesEnCours] = useState({});
  const [leadOuvert, setLeadOuvert] = useState(null);

  async function changerStatut(lead, statut) {
    await leadsApi.update(lead.id, { statut, assigne_a: lead.assigne_a, notes_internes: lead.notes_internes });
    refetch();
  }

  async function enregistrerNotes(lead) {
    const notes = notesEnCours[lead.id] ?? lead.notes_internes ?? '';
    await leadsApi.update(lead.id, { statut: lead.statut, assigne_a: lead.assigne_a, notes_internes: notes });
    refetch();
  }

  async function supprimerDemande(lead) {
    if (!window.confirm('Supprimer définitivement cette demande ? Cette action est irréversible.')) return;
    await leadsApi.remove(lead.id);
    if (leadOuvert?.id === lead.id) setLeadOuvert(null);
    refetch();
  }

  async function exporterCsv() {
    const token = localStorage.getItem('access_token');
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/admin/leads-export?statut=${filtreStatut}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'demandes.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  }

  if (loading) return <p>Chargement...</p>;

  return (
    <section>
      <div className="admin-toolbar">
        <h1>Demandes (devis, rappel, contact)</h1>
        <button type="button" className="leads-export-btn" onClick={exporterCsv}>
          <Download size={16} aria-hidden="true" />
          Exporter CSV
        </button>
      </div>

      <div className="filter-chips" role="group" aria-label="Filtrer par statut">
        <button
          type="button"
          className={`filter-chip ${!filtreStatut ? 'filter-chip--active' : ''}`}
          onClick={() => setFiltreStatut('')}
        >
          Tous les statuts
        </button>
        {STATUTS.map((s) => (
          <button
            key={s}
            type="button"
            className={`filter-chip ${filtreStatut === s ? 'filter-chip--active' : ''}`}
            onClick={() => setFiltreStatut(s)}
          >
            {STATUT_META[s].label}
          </button>
        ))}
      </div>

      {!leads?.length && <p className="leads-empty">Aucune demande pour le moment.</p>}

      <div className="leads-grid">
        {leads?.map((lead) => {
          const meta = STATUT_META[lead.statut] || STATUT_META.nouveau;
          const entries = Object.entries(lead.donnees_json || {});

          return (
            <article
              key={lead.id}
              className={`lead-ticket lead-ticket--${meta.tone}`}
              onClick={() => setLeadOuvert(lead)}
            >
              <header className="lead-ticket__head">
                <span className="lead-ticket__icon">
                  <TypeIcon type={lead.formulaire_type} size={18} />
                </span>
                <div className="lead-ticket__title">
                  <span className="lead-ticket__form">{lead.formulaire_nom}</span>
                  <span className="lead-ticket__id">N° {String(lead.id).padStart(5, '0')}</span>
                </div>
                <StatutSelect lead={lead} meta={meta} onChange={changerStatut} />
              </header>

              <div className="lead-ticket__perforation" aria-hidden="true">
                <span className="lead-ticket__notch lead-ticket__notch--left" />
                <span className="lead-ticket__notch lead-ticket__notch--right" />
              </div>

              <dl className="lead-ticket__preview">
                {entries.slice(0, 3).map(([cle, valeur]) => (
                  <div key={cle} className="lead-ticket__preview-row">
                    <dt>{cle}</dt>
                    <dd>{String(valeur)}</dd>
                  </div>
                ))}
                {entries.length > 3 && (
                  <p className="lead-ticket__more">+{entries.length - 3} autre(s) champ(s)</p>
                )}
              </dl>

              <footer className="lead-ticket__foot">
                <span className="lead-ticket__date">{formatDate(lead.created_at)}</span>
                <div className="lead-ticket__actions">
                  <button
                    type="button"
                    className="lead-ticket__icon-btn"
                    onClick={(e) => { e.stopPropagation(); setLeadOuvert(lead); }}
                    aria-label="Voir les détails de la demande"
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    type="button"
                    className="lead-ticket__icon-btn lead-ticket__icon-btn--danger"
                    onClick={(e) => { e.stopPropagation(); supprimerDemande(lead); }}
                    aria-label="Supprimer la demande"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </footer>
            </article>
          );
        })}
      </div>

      <Modal open={!!leadOuvert} onClose={() => setLeadOuvert(null)} ariaLabel="Détails de la demande">
        {leadOuvert && (
          <div className="lead-modal">
            <div className="lead-modal__head">
              <span className="lead-ticket__icon lead-ticket__icon--lg">
                <TypeIcon type={leadOuvert.formulaire_type} size={20} />
              </span>
              <div>
                <h2 className="lead-modal__title">{leadOuvert.formulaire_nom}</h2>
                <span className="lead-ticket__id">N° {String(leadOuvert.id).padStart(5, '0')}</span>
              </div>
            </div>
            <p className="lead-modal__date">Reçue le {formatDate(leadOuvert.created_at)}</p>

            <StatutSelect
              lead={leadOuvert}
              meta={STATUT_META[leadOuvert.statut] || STATUT_META.nouveau}
              onChange={(lead, statut) => {
                changerStatut(lead, statut);
                setLeadOuvert({ ...lead, statut });
              }}
            />

            <dl className="lead-modal__donnees">
              {Object.entries(leadOuvert.donnees_json || {}).map(([cle, valeur]) => (
                <div key={cle} className="lead-modal__donnees-row">
                  <dt>{cle}</dt>
                  <dd>{String(valeur)}</dd>
                </div>
              ))}
            </dl>

            <label className="lead-modal__notes-label" htmlFor="lead-modal-notes">Notes internes</label>
            <textarea
              id="lead-modal-notes"
              rows={4}
              defaultValue={leadOuvert.notes_internes || ''}
              onChange={(e) => setNotesEnCours({ ...notesEnCours, [leadOuvert.id]: e.target.value })}
              onBlur={() => enregistrerNotes(leadOuvert)}
            />

            <div className="lead-modal__actions">
              <button
                type="button"
                className="lead-modal__delete"
                onClick={() => supprimerDemande(leadOuvert)}
              >
                <Trash2 size={16} aria-hidden="true" />
                Supprimer cette demande
              </button>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}
